import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { FORM_ERROR } from 'final-form';
import { TextField, Switches } from 'mui-rff';
import { Form } from 'react-final-form';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import { showSuccessMessage } from '@store/dashboard';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import {
  addNewPropertyKey,
  getPropertyKeyDetails,
  paymentOptionsPropertyKeysActions,
  selectSelectedPropertyKey,
  updatePropertyKey,
} from '../../store/payment-options-property-keys';
import {
  addPropertyKey,
  editPropertyKey,
  PROPERTY_KEY_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';

import FormSaveAndSubmit from '../../../../components/FormSaveAndSubmit';
import { Typography } from '../../../../components/styledComponents';

const initForm = {
  key: '',
  description: '',
  sendPropertyToUi: false,
};

export default function PropertyKeyForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedPropertyKey = useSelector(selectSelectedPropertyKey);

  const { id } = useParams();

  const permissions = usePermissions();
  const canEdit = permissions?.canEditPaymentOptions;

  // A view-only user can still open an existing record, but must not reach the
  // create form directly via its URL.
  useEffect(() => {
    if (id === undefined && !permissions?.canEditPaymentOptions) {
      navigate(PROPERTY_KEY_LIST_ROUTE);
    }
  }, [id, navigate, permissions]);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [formValue, setFormValue] = useState(initForm);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  const formRef = useRef(null);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(breadcrumbsActions.showMenu(true));

    if (id !== undefined) {
      return Promise.all([
        dispatch(breadcrumbsActions.setBreadcrumbs(editPropertyKey(id))),
        dispatch(getPropertyKeyDetails(id)),
      ]).then(() => setIsFetchingData(false));
    }

    return Promise.all([
      dispatch(breadcrumbsActions.setBreadcrumbs(addPropertyKey)),
      dispatch(paymentOptionsPropertyKeysActions.resetSelectedPropertyKey()),
    ]).then(() => setIsFetchingData(false));
  }, [id, dispatch]);

  useEffect(() => {
    if (id !== undefined) {
      setFormValue(selectedPropertyKey);
    } else {
      setFormValue(initForm);
    }
  }, [selectedPropertyKey, id]);

  const onSubmit = useCallback(
    (values) => {
      setFormSuccessMsg('');
      setFormErrorMsg('');

      const payload = {
        id,
        key: values.key,
        description: values.description,
        isSendPropertyToUi: values.sendPropertyToUi,
      };

      return dispatch(
        id !== undefined
          ? updatePropertyKey(payload)
          : addNewPropertyKey(payload),
      ).then((data) => {
        if (data.payload && data.payload.status === 'SUCCESS') {
          if (id === undefined) {
            dispatch(
              showSuccessMessage(
                `Property key ${
                  id !== undefined ? 'updated' : 'added'
                } successfully`,
              ),
            );
            navigate(PROPERTY_KEY_LIST_ROUTE);
          } else {
            setFormSuccessMsg('Property keys been updated successfully');
            setFormValue(values); // needed to disable buttons after successful submit
          }
        } else {
          if (data.payload) setFormErrorMsg(data.payload.message);
          if (data.error) setFormErrorMsg(data.error.message);
        }
      });
    },
    [dispatch, id, navigate],
  );

  const handleReset = useCallback(() => {
    setFormSuccessMsg('');
    setFormErrorMsg('');
    formRef.current.reset();
  }, []);

  const validate = (values) => {
    if (!values.key) return { [FORM_ERROR]: 'Property key is required' };
    if (!values.description)
      return { [FORM_ERROR]: 'Property key description is required' };
    return {};
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title={
        id !== undefined ? selectedPropertyKey.description : 'New Property Key'
      }
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formValue}
          validate={validate}
          render={({ handleSubmit, pristine, invalid, form, values }) => {
            formRef.current = form;
            return (
              <form
                onSubmit={(e) =>
                  process.env.REACT_APP_ENV !== 'production'
                    ? handleSubmit(e)
                    : handlePreSubmit(e)
                }
                noValidate>
                <ProdConfirmationDialog
                  openDialog={openDialog}
                  yesHandler={handleSubmit}
                />
                <Grid
                  container
                  style={{
                    alignContent: 'center',
                    justifyContent: 'space-between',
                  }}>
                  <Grid
                    item
                    xs={12}
                    md={8}
                    style={{ flex: 2 }}>
                    {formSuccessMsg && (
                      <Alert
                        onClose={() => {
                          setFormSuccessMsg('');
                        }}
                        severity="success">
                        {formSuccessMsg}
                      </Alert>
                    )}
                    {formErrorMsg && (
                      <Alert
                        onClose={() => {
                          setFormErrorMsg('');
                        }}
                        severity="error">
                        {formErrorMsg}
                      </Alert>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    style={{ display: 'flex' }}>
                    <FormSaveAndSubmit
                      id={id}
                      resetDisabled={pristine}
                      submitDisabled={pristine || invalid || !canEdit}
                      handleReset={handleReset}
                    />
                  </Grid>
                </Grid>
                <Box
                  style={{
                    marginTop: '20px',
                    padding: '32px',
                    border: '1px solid #c4c4c4',
                    borderRadius: '4px',
                  }}>
                  <Typography
                    className="form-header"
                    variant="formHeader">
                    Details
                  </Typography>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={8}>
                      <TextField
                        required
                        aria-label="property-key"
                        name="key"
                        variant="outlined"
                        label="Key"
                        disabled={id !== undefined}
                      />
                      <TextField
                        style={{ marginTop: '16px' }}
                        required
                        aria-label="property-key-description"
                        name="description"
                        variant="outlined"
                        label="Description"
                      />
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      md={4}>
                      <Switches
                        name="sendPropertyToUi"
                        required
                        data={{
                          label: 'Send Property to UI?',
                          value: values.sendPropertyToUi,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </form>
            );
          }}
        />
      )}
    </ApplicationPageTemplate>
  );
}
